import React from 'react'
import { Drawer, Descriptions, Divider, Space, Button, Tag, Badge } from 'antd'
import { CheckCircleOutlined } from '@ant-design/icons'
import type { Dispute } from '../types/dispute.types'
import { DisputeConversation } from './DisputeConversation'

interface DisputeDetailsDrawerProps {
    open: boolean
    onClose: () => void
    dispute: Dispute | null
    onAssign: () => void
    onResolve: () => void
    onSendMessage: (values: any) => void
}

export const DisputeDetailsDrawer: React.FC<DisputeDetailsDrawerProps> = ({
    open,
    onClose,
    dispute,
    onAssign,
    onResolve,
    onSendMessage
}) => {
    if (!dispute) return null

    return (
        <Drawer
            title="Dispute Details"
            placement="right"
            width={800}
            onClose={onClose}
            open={open}
        >
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Descriptions title="Dispute Information" column={1} bordered>
                    <Descriptions.Item label="Dispute ID">
                        <strong>{dispute.id}</strong>
                    </Descriptions.Item>
                    <Descriptions.Item label="Order ID">{dispute.orderId}</Descriptions.Item>
                    <Descriptions.Item label="Customer">{dispute.customer}</Descriptions.Item>
                    <Descriptions.Item label="Merchant">{dispute.merchant}</Descriptions.Item>
                    <Descriptions.Item label="Subject">{dispute.subject}</Descriptions.Item>
                    <Descriptions.Item label="Description">
                        {dispute.description || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Priority">
                        <Tag
                            color={
                                dispute.priority === 'critical'
                                    ? 'purple'
                                    : dispute.priority === 'high'
                                        ? 'red'
                                        : dispute.priority === 'medium'
                                            ? 'orange'
                                            : 'blue'
                            }
                        >
                            {dispute.priority.toUpperCase()}
                        </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Status">
                        <Tag
                            color={
                                dispute.status === 'resolved'
                                    ? 'green'
                                    : dispute.status === 'in_progress'
                                        ? 'blue'
                                        : dispute.status === 'closed'
                                            ? 'gray'
                                            : 'orange'
                            }
                        >
                            {dispute.status.replace('_', ' ').toUpperCase()}
                        </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Created">{dispute.createdAt}</Descriptions.Item>
                    <Descriptions.Item label="Age">
                        <Badge
                            color={dispute.ageHours > 48 ? 'red' : dispute.ageHours > 24 ? 'orange' : 'blue'}
                            text={`${dispute.ageHours} hours`}
                        />
                    </Descriptions.Item>
                    <Descriptions.Item label="Assigned To">
                        {dispute.assignedTo || <Tag>Unassigned</Tag>}
                    </Descriptions.Item>
                    {dispute.resolution && (
                        <Descriptions.Item label="Resolution">
                            {dispute.resolution}
                        </Descriptions.Item>
                    )}
                </Descriptions>

                <Divider />

                <DisputeConversation
                    dispute={dispute}
                    onSendMessage={onSendMessage}
                />

                <Divider />

                <Space>
                    {!dispute.assignedTo && (
                        <Button type="primary" onClick={onAssign}>
                            Assign to Me
                        </Button>
                    )}
                    {dispute.status !== 'resolved' && dispute.status !== 'closed' && (
                        <Button type="primary" icon={<CheckCircleOutlined />} onClick={onResolve}>
                            Mark as Resolved
                        </Button>
                    )}
                </Space>
            </Space>
        </Drawer>
    )
}
